
import { useLanguageContext } from '../../contexts/LanguageContext';
import Dialog, { DialogSectionTitle, IDialogProps } from '../Dialog';
import { ActionButton } from '../Button';
import { Avatar, Divider, Grid, MenuItem, Typography } from '@mui/material';
import SelectField from '../SelectField';
import { useUserContext } from '../../contexts/UserContext';
import Languages from '../../data/Languages';
import { useState, useEffect, useCallback, useMemo } from 'react';
import FormHelper from '../../helpers/FormHelper';
import UserService from '../../services/UserService';
import DefaultConstants from '../../data/Constants';
import Navigation from '../../data/Navigation';
import { INavigationLink } from '../../interfaces/INavigation';
import FormOption from '../FormOption';
import useForceRender from '../../hooks/useForceRender';
import IPermission from '../../interfaces/IPermission';
import { useEnvironmentContext } from '../../contexts/EnvironmentContext';
import StringHelper from '../../helpers/StringHelper';
import IPartnerPms from '../../interfaces/IPartnerPms';


type ShortcutType = Record<number, IPermission[]>;


const PreferencesDialog: React.FunctionComponent<Pick<IDialogProps, 'title' | 'open' | 'handleClose'>> = ({ title, open, handleClose }) => {
    const { environment } = useEnvironmentContext();
    const { translate, handleChangeLanguage } = useLanguageContext();
    const { user } = useUserContext();

    const forceRender = useForceRender();

    const [language, setLanguage] = useState<string | undefined>(undefined);
    const [rowsPerPage, setRowsPerPage] = useState<number | undefined>(undefined);
    const [shortcuts, setShortcuts] = useState<ShortcutType>({});


    useEffect(() => {
        if (open) {
            setLanguage(user?.language);
            setRowsPerPage(user?.rowsPerPage);

            const shortcutAux = (user?.enterprises.reduce((prev, curr) => ({ ...prev, [curr.id]: [...curr.shortcuts]}), {}) ?? {}) as ShortcutType;
            setShortcuts(shortcutAux);
        }

        // eslint-disable-next-line
    }, [open]);


    const handleChangeShortcuts = useCallback((enterpriseId: number, permission: IPermission) => {
        const shortcutsEnterpriseAux = shortcuts[enterpriseId] ?? [];
        const shortcutsEnterpriseAliasAux = shortcutsEnterpriseAux.map(s => s.alias);
        
        if (shortcutsEnterpriseAliasAux.includes(permission.alias)) {
            const index = shortcutsEnterpriseAliasAux.findIndex(p => p === permission.alias);
            shortcutsEnterpriseAux.splice(index, 1);
        } else {
            shortcutsEnterpriseAux.push(permission);
        }

        setShortcuts({
            ...shortcuts,
            [enterpriseId]: shortcutsEnterpriseAux
        });

        forceRender();

        // eslint-disable-next-line
    }, [shortcuts]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const formData = FormHelper.Create(event);
        UserService.SavePreferences(formData).then(() => {
            if (user) {
                user.rowsPerPage = rowsPerPage as number;
                user.language = language as string;

                Object.entries(shortcuts).forEach(shortcut => {
                    const [enterpriseId, permissions] = shortcut;
                    const enterpriseIndex = user.enterprises.findIndex(e => e.id.toString() === enterpriseId);
                    user.enterprises[enterpriseIndex].shortcuts = permissions;
                });

                handleChangeLanguage(user.language);
                handleClose();
            }
        });
        // eslint-disable-next-line
    }, [language, rowsPerPage, shortcuts]);


    return (
        <Dialog 
            open={open}
            handleClose={handleClose}
            title={title}
            fullWidth={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
            maxWidth='sm'
            fullHeight={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={6} xs={12}>
                    <SelectField
                        label={translate('language')}
                        name='language'
                        value={language}
                        required={true}
                        onChange={e => setLanguage(e.target.value)}
                        customData={true}
                        data={Languages.map((lang, index) => {
                            return (
                                <MenuItem value={lang.value} key={index}>
                                    <Avatar src={lang.icon} alt={lang.initials} className='selectfield-default-menuitem-avatar' />
                                    {translate(lang.text)}
                                </MenuItem>
                            );
                        })}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <SelectField
                        label={translate('recordsPerPage')}
                        name='rowsPerPage'
                        value={rowsPerPage}
                        required={true}
                        onChange={e => setRowsPerPage(e.target.value)}
                        data={[10, 20, 30, 50, 100].map(number => {
                            return {
                                value: number,
                                text: `${number} ${translate('records')}`
                            };
                        })}
                    />
                </Grid>

                <Grid item xs={12} marginY={2}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={translate('shortcuts')} />
                </Grid>

                {
                    user?.enterprises.map(e => {
                        return (
                            <Grid item xs={12} marginTop={1} marginBottom={3} key={e.id}>
                                <Typography variant='subtitle1' component='span' className='fontsize-14 fontweight-600' paddingLeft={0.4}>
                                    {e.name}
                                </Typography>

                                <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing} marginTop={-1}>
                                    {
                                        Navigation
                                            .reduce((prev, curr) => ([...prev, ...curr.items]), [] as INavigationLink[])
                                            .reduce((prev, curr) => {
                                                if (curr.items !== null) {
                                                    prev.push(...curr.items);
                                                } else {
                                                    prev.push(curr);
                                                }
                                                
                                                return prev;
                                            }, [] as INavigationLink[])
                                            .map((n, index) => {
                                                const permission = e.permissions.find(p => p.alias.includes(n.alias[0]));
                                                if (!permission) {
                                                    return null;
                                                }

                                                const shortcutsEnterprise = shortcuts[e.id]?.map(s => s.alias) ?? [];
                                                const selected = shortcutsEnterprise.indexOf(permission.alias) > -1;
                                                const disabled = !selected && shortcutsEnterprise.length >= 5;

                                                const moduleName = StringHelper.FirstLetterLowerCase(permission.alias.split('.')[0]);

                                                return (
                                                    <Grid item sm={4} xs={6} key={permission.id}>
                                                        <FormOption
                                                            title={translate(n.name)}
                                                            type='checkbox'
                                                            name={`shortcuts[${e.id}][${index}]`}
                                                            value={permission.id}
                                                            selected={selected}
                                                            disabled={disabled}
                                                            onClick={() => handleChangeShortcuts(e.id, permission)}
                                                            Icon={n.Icon ?? undefined}
                                                            orientation='vertical'
                                                            subtitle={environment.partnerPms[`${moduleName}ModuleName` as keyof(IPartnerPms)].toString()}
                                                        />
                                                    </Grid>
                                                );
                                            })
                                    }
                                </Grid>
                            </Grid>
                        )
                    })
                }
            </Grid>
        </Dialog>
    );
}

export default PreferencesDialog;